import React, { useState, useMemo } from 'react';
import { useAdminContext } from '../../../context/AdminContext';
import Pagination from '../../../components/common/Pagination.js';
import { AlertCircle, Search } from 'lucide-react';
import TableSkeleton from '../../../components/common/TableSkeleton'; // <-- 1. Importar el esqueleto
import '../../../styles/Reportes.css';


const InversionesReport = () => {
    const { allAgreements, users, loading } = useAdminContext() || { allAgreements: [], users: [], loading: true };
    const [searchTermUser, setSearchTermUser] = useState('');
    const [searchTermAmount, setSearchTermAmount] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 15;

    const userMap = useMemo(() => {
        if (!users) return new Map();
        return new Map(users.map(user => [user.id, user.username]));
    }, [users]);

    const filteredAgreements = useMemo(() => {
        let agreements = (allAgreements || []).filter(a => a.status === 'aprobado');

        if (searchTermUser) {
            const lowercasedFilter = searchTermUser.toLowerCase();
            agreements = agreements.filter(a => {
                const username = userMap.get(a.userId)?.toLowerCase();
                return a.userEmail?.toLowerCase().includes(lowercasedFilter) ||
                       (username && username.includes(lowercasedFilter));
            });
        }

        if (searchTermAmount) {
            agreements = agreements.filter(a => a.amount.toString().includes(searchTermAmount));
        }

        return agreements;
    }, [allAgreements, searchTermUser, searchTermAmount, userMap]);

    const totalPages = Math.ceil(filteredAgreements.length / ITEMS_PER_PAGE);
    const paginatedAgreements = filteredAgreements.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // --- 2. Usar el esqueleto mientras se carga ---
    if (loading) {
        return <div className="report-container"><TableSkeleton columns={5} rows={10} /></div>;
    }

    return (
        <div className="report-container">
            <div className="report-filters">
                <div className="filter-item">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Buscar por usuario o email..."
                        value={searchTermUser}
                        onChange={(e) => setSearchTermUser(e.target.value)}
                    />
                </div>
                <div className="filter-item">
                    <Search size={16} />
                    <input
                        type="number"
                        placeholder="Buscar por monto..."
                        value={searchTermAmount}
                        onChange={(e) => setSearchTermAmount(e.target.value)}
                    />
                </div>
            </div>

            {filteredAgreements.length > 0 ? (
                <>
                    <div className="report-table-container">
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Usuario</th>
                                    <th>Email</th>
                                    <th>Monto</th>
                                    <th>Tipo</th>
                                    <th>Fecha de Aprobación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedAgreements.map(agreement => (
                                    <tr key={agreement.id}>
                                        <td>{userMap.get(agreement.userId) || 'N/A'}</td>
                                        <td>{agreement.userEmail}</td>
                                        <td>${agreement.amount.toFixed(2)}</td>
                                        <td>
                                            <span className={`badge ${agreement.isTest ? 'badge-test' : 'badge-real'}`}>
                                                {agreement.isTest ? 'Prueba' : 'Real'}
                                            </span>
                                        </td>
                                        <td>{agreement.approvedAt?.toDate().toLocaleDateString('es-ES') || 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                         totalItems={filteredAgreements.length}
                         itemsPerPage={ITEMS_PER_PAGE}
                         currentPage={currentPage}
                         onNext={handleNextPage}
                         onPrevious={handlePreviousPage}
                         canNext={currentPage < totalPages}
                         canPrevious={currentPage > 1}
                    />
                </>
            ) : (
                <div className="report-empty-state">
                    <AlertCircle size={40} />
                    <p>No se encontraron participaciones que coincidan con los filtros.</p>
                </div>
            )}
        </div>
    );
};

export default InversionesReport;
